/**
 * PriMessageBubble — Renders a single pRI chat message.
 * User messages: right-aligned, blue bg.
 * Assistant messages: left-aligned, gray bg, robot avatar.
 * Lightweight inline markdown: bold, italic, inline code, code blocks, links.
 */
import type { PriMessage } from '@/stores/pri-store';
import { cn } from '@kgc/ui';
import { useMemo } from 'react';

interface PriMessageBubbleProps {
  message: PriMessage;
}

/** Format Date to HH:MM */
function formatTime(timestamp: Date): string {
  const d = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Minimal markdown-to-HTML renderer for chat messages.
 * Handles: **bold**, *italic*, `inline code`, ```code blocks```, and [links](url).
 * Sanitizes HTML entities to prevent XSS.
 */
function renderMarkdown(text: string): string {
  // Escape HTML entities
  let html = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // Code blocks (```...```)
  html = html.replace(
    /```(?:\w*)\n?([\s\S]*?)```/g,
    '<pre class="my-1 overflow-x-auto rounded bg-slate-900/60 px-2 py-1 text-xs"><code>$1</code></pre>'
  );

  // Inline code (`...`)
  html = html.replace(
    /`([^`]+)`/g,
    '<code class="rounded bg-slate-900/60 px-1 py-0.5 text-xs">$1</code>'
  );

  // Bold (**...**)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Italic (*...*)
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');

  // Links [text](url)
  html = html.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-blue-400 hover:text-blue-300">$1</a>'
  );

  // Newlines to <br>
  html = html.replace(/\n/g, '<br>');

  return html;
}

export function PriMessageBubble({ message }: PriMessageBubbleProps) {
  const isUser = message.role === 'user';
  const renderedContent = useMemo(() => renderMarkdown(message.content), [message.content]);

  return (
    <div className={cn('flex gap-2 px-3 py-1', isUser ? 'flex-row-reverse' : 'flex-row')}>
      {/* Avatar (assistant only) */}
      {!isUser && (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-sm">
          🤖
        </div>
      )}

      {/* Bubble */}
      <div
        className={cn(
          'max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'rounded-br-md bg-blue-600 text-white'
            : 'rounded-bl-md bg-slate-700/60 text-slate-200 dark:bg-slate-700/60'
        )}
      >
        {/* Content */}
        <div
          className="break-words [&_a]:underline [&_pre]:whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: renderedContent }}
        />

        {/* Timestamp */}
        <div
          className={cn(
            'mt-1 text-[10px]',
            isUser ? 'text-right text-blue-200/60' : 'text-slate-500'
          )}
        >
          {formatTime(message.timestamp)}
        </div>
      </div>
    </div>
  );
}
